/**
 * English Privacy Policy — Digital Nutrition Corp. / SoundPill
 * Effective: September 26, 2023 (with health-data provisions as updated)
 */
export default function PrivatePolicyEnPage() {
  return (
    <main className="w-full min-h-full overflow-y-auto no-scrolbar bg-N0 text-N9">
      <div className="my-5 mx-auto max-w-[820px] px-4 leading-6 font-1 text-sm space-y-4 md:pt-8">
        <p>
          <strong>SoundPill Privacy Policy</strong>
        </p>

        <p>
          <strong>Significance of the Privacy Policy</strong>
        </p>
        <p>
          Digital Nutrition Corp. (hereinafter the “Company”) complies with all laws and regulations related to the
          protection of personal information, including the Personal Information Protection Act, the Protection of
          Communications Secrets Act, the Telecommunications Business Act, and the Act on Promotion of Information and
          Communications Network Utilization and Information Protection (hereinafter the “Network Act”), as well as
          notices, directives, and guidelines issued by public authorities. This Privacy Policy transparently explains
          matters related to “personal information,” including what information the Company collects, how the collected
          information is used, with whom it is shared when necessary (“entrusted or provided”), and when and how
          information is destroyed after the purpose of use has been achieved.
        </p>

        <p>
          <strong>Consent to the Collection and Use of Personal Information</strong>
        </p>
        <p>
          To collect the minimum personal information necessary for the establishment and performance of the service
          use agreement and information that can identify the user through lawful means, the Company provides this
          Privacy Policy containing matters related to the collection and use of personal information at the time of
          membership registration. If the user selects the “Agree” button, the user is deemed to have consented
          thereto.
        </p>

        <p>
          <strong>Article 1 [Items and Methods of Collecting Personal Information]</strong>
        </p>
        <p>
          The Company collects and processes personal information as follows for membership registration, customer
          support, paid service payments, and the provision and performance of various services and contracts.
        </p>
        <ol className="list-decimal pl-5 space-y-3">
          <li>
            Items of personal information collected
            <ul className="list-disc pl-5 mt-2 space-y-2">
              <li>
                At membership registration — Required items
                <br />
                - When signing up via social login,
                <br />
                Required: social media ID in the user’s own name (or an email-format ID), social login identifier,
                phone number
                <br />
                Optional: name (or nickname)
                <br />
                - When signing up via email,
                <br />
                Required: email address used, password, and name (or nickname)
              </li>
              <li>
                Automatically generated information — The following information may be generated and collected during
                service use: app push permission; model name of mobile phones and devices with smart OS, OS
                information, device token, Google Advertising ID (ADID), Apple Advertising Identifier (IDFA); IP
                address, cookies, visit date/time, records of improper use, and service usage records.
              </li>
              <li>
                For paid service payments, credit card information, bank transfer information, carrier information,
                gift voucher numbers, and other payment-related information may inevitably be collected when necessary
                for payment, etc.
              </li>
              <li>
                For events, promotions, prize shipping, or service inquiries, additional information may be collected
                after separate notice of the purpose of use and retention period of the personal information collected
                and after obtaining consent.
              </li>
              <li>
                Health / biosignal information (when the relevant features are used and permissions are granted): heart
                rate, heart rate variability (HRV), wellness/stress-related indicators derived therefrom, and
                information linked from health platforms and wearables.
              </li>
            </ul>
          </li>
          <li>
            Methods of collecting personal information — When collecting personal information, the Company always
            informs the user in advance of the purpose of collection and use, items collected, and retention and use
            period, and obtains consent. Personal information is collected through the following methods:
            <ul className="list-disc pl-5 mt-2 space-y-2">
              <li>
                When the user consents to the collection and use of personal information and directly enters
                information during membership registration and service use
              </li>
              <li>When information is provided by partners or affiliates due to affiliate services, etc.</li>
              <li>
                When automatically generated information is created and collected during use of mobile applications,
                websites, etc.
              </li>
              <li>Other collection through the user’s voluntary consent and provision during service use</li>
            </ul>
          </li>
        </ol>

        <p>
          <strong>Article 2 [Purpose of Collecting Personal Information]</strong>
        </p>
        <p>The Company uses collected personal information for the following purposes:</p>
        <ol className="list-decimal pl-5 space-y-2">
          <li>
            Performance of contracts related to service provision; fee settlement for paid services; provision of
            content; billing for paid service use; purchases and payments
          </li>
          <li>
            Personal identification for membership services; prevention of unauthorized use; confirmation of duplicate
            registration; retention of records for dispute resolution; handling of complaints and other grievances;
            delivery of notices
          </li>
          <li>
            Development of new services and provision of customized services; verification of service effectiveness;
            analysis of access frequency; statistics on members’ service use; provision of event and advertising
            information; coupon delivery
          </li>
        </ol>

        <p>
          <strong>Article 3 [Retention and Use Period of Personal Information]</strong>
        </p>
        <p>
          If a member requests withdrawal or withdraws consent to the collection and use of personal credit
          information, or if the purpose of collection and use has been achieved or the retention period has ended, the
          Company destroys the relevant personal credit information without delay. However, the following information
          may be retained for the periods stated below:
        </p>
        <ol className="list-decimal pl-5 space-y-2">
          <li>
            Membership registration and management: from registration until membership withdrawal (provided that, if
            any of the following applies, until such reason ends)
          </li>
          <li>
            Until the end of any investigation related to a violation of applicable laws
          </li>
          <li>
            Until settlement of claims and liabilities such as refunds: essential information such as name, mobile
            phone number, and account number may be used
          </li>
          <li>
            Service provision: until completion of service supply and payment/settlement (provided that, if any of the
            following applies, until the end of the relevant period)
            <ul className="list-disc pl-5 mt-2 space-y-1">
              <li>Records of electronic financial transactions: 5 years</li>
              <li>Records of contracts or withdrawal of offers, payments, and supply of goods, etc.: 5 years</li>
              <li>Records of consumer complaints or dispute handling: 3 years</li>
              <li>Records related to display/advertising: 6 months</li>
              <li>Website visit records: 3 months</li>
              <li>Advertising / marketing: until consent is withdrawn or membership is terminated</li>
            </ul>
          </li>
        </ol>
        <p>
          Even if the application is deleted, personal information may remain if membership withdrawal is not
          requested. If you wish to completely delete your personal information, you must request membership
          withdrawal.
        </p>

        <p>
          <strong>Article 4 [Handling and Entrustment of Collected Personal Information]</strong>
        </p>
        <p>
          To improve services, the Company entrusts personal information to external contractors or services as
          follows, and such personal information is destroyed without delay upon completion of the entrusted work.
        </p>

        <p>
          <strong>
            Article 5 [Installation, Operation, and Rejection of Automatic Personal Information Collection Devices]
          </strong>
        </p>
        <ol className="list-decimal pl-5 space-y-3">
          <li>
            The Company may operate “cookies” (connection information files) and similar technologies that store and
            retrieve user information from time to time to provide specialized customized services. In connection with
            cookie operations, the Company may identify the user’s computer but does not personally identify the user.
          </li>
          <li>
            Where cookie functions cannot be used, such as in mobile applications, technologies that perform functions
            similar to cookies (e.g., advertising identifiers) may be used.
            <ul className="list-disc pl-5 mt-2 space-y-2">
              <li>
                Purpose of cookies, etc.: analysis of access frequency and visit time; identification of preferences
                and areas of interest and tracking of activity; targeted marketing and personalized services based on
                event participation and visit frequency, etc.
              </li>
              <li>
                Installation, operation, and rejection of cookies, etc.: Users have a choice regarding cookies. Users
                may set the cookie acceptance level in the web browser under [Tools → Internet Options → Privacy →
                Advanced]. However, if storage of all cookies is refused, provision of some services may be difficult.
              </li>
            </ul>
          </li>
          <li>
            The Company may use automated systems that analyze users’ service usage information to provide customized
            content, personalized advertising, and other functions tailored to how the service is used. The Company may
            also analyze service usage information to detect abuse such as spam, malware, and illegal access.
          </li>
        </ol>

        <p>
          <strong>Article 6 [Procedures and Methods for Destroying Personal Information]</strong>
        </p>
        <p>
          In principle, the Company destroys personal information without delay once the purpose of processing has been
          achieved.
        </p>
        <ol className="list-decimal pl-5 space-y-3">
          <li>
            The Company destroys personal information by the following methods:
            <ul className="list-disc pl-5 mt-2 space-y-1">
              <li>Electronic files: deletion of files and formatting of storage media such as disks</li>
            </ul>
          </li>
          <li>
            Procedures for destroying personal information of dormant accounts
            <ul className="list-disc pl-5 mt-2 space-y-2">
              <li>
                In accordance with applicable laws, accounts that have not logged in to the service for one year or
                more are converted to dormant accounts and managed separately for personal information protection.
              </li>
              <li>
                Member information subject to dormancy: all information collected/managed at registration or through
                member information updates
              </li>
              <li>
                The Company notifies the relevant user by email or other means one month before the separate storage
                period for dormant account personal information begins.
              </li>
              <li>
                Personal information of dormant accounts is destroyed without delay three years after the start of
                separate storage. However, personal information that has not been destroyed will be provided again when
                the user requests resumption of service use.
              </li>
            </ul>
          </li>
        </ol>

        <p>
          <strong>Article 7 [Measures to Ensure the Security of Personal Information]</strong>
        </p>
        <p>
          In handling users’ personal information, the Company takes managerial and technical measures necessary to
          ensure that personal information is not lost, stolen, leaked, forged, altered, or damaged.
        </p>
        <ol className="list-decimal pl-5 space-y-3">
          <li>
            Managerial measures
            <ul className="list-disc pl-5 mt-2 space-y-1">
              <li>Establishment and implementation of an internal management plan</li>
              <li>Minimization of staff who process personal information and related training</li>
            </ul>
          </li>
          <li>
            Technical measures
            <ul className="list-disc pl-5 mt-2 space-y-1">
              <li>Access rights management for personal information processing systems, etc.</li>
              <li>Installation of access control systems</li>
              <li>Encryption of unique identifiers, etc., or equivalent measures</li>
              <li>Installation and updating of security programs</li>
              <li>Retention of access logs and measures to prevent forgery or alteration</li>
            </ul>
          </li>
        </ol>

        <p>
          <strong>Article 8 [Rights of Users and Legal Representatives and Methods of Exercise]</strong>
        </p>
        <ol className="list-decimal pl-5 space-y-3">
          <li>
            Users may at any time exercise the following personal information protection rights against the Company:
            <ul className="list-disc pl-5 mt-2 space-y-1">
              <li>Request to access personal information</li>
              <li>Request for correction if there are errors, etc.</li>
              <li>Request for deletion</li>
              <li>Request to suspend processing</li>
            </ul>
          </li>
          <li>
            Rights under the above items may be exercised against the Company in writing, by telephone, or by email
            (share@digitalnutrition.info), and the Company will take measures without delay.
          </li>
          <li>
            If a user requests correction or deletion of errors in personal information, etc., the Company will not use
            or provide such personal information until the correction or deletion is completed.
          </li>
          <li>
            Rights under the above items may be exercised through a legal representative of the user or an agent
            authorized by the user. In such cases, a power of attorney in the form attached as Form No. 11 of the
            Enforcement Rules of the Personal Information Protection Act must be submitted.
          </li>
          <li>
            Users shall not infringe on the personal information or privacy of themselves or others that the Company is
            processing, in violation of the Personal Information Protection Act or other related laws.
          </li>
        </ol>

        <p>
          <strong>Article 9 [Designation of the Personal Information Protection Officer]</strong>
        </p>
        <p>
          To protect users’ personal information and handle complaints related to personal information, the Company
          designates a Personal Information Protection Officer as follows. Users may contact the Personal Information
          Protection Officer regarding personal information matters arising from use of the Company’s services.
        </p>
        <ol className="list-decimal pl-5 space-y-3">
          <li>
            Personal Information Protection Officer — Name: Kim Min-je (Head of Development); Contact:
            share@digitalnutrition.info
          </li>
          <li>
            For other reports or counseling regarding personal information infringement, please contact the following
            institutions:
            <ul className="list-disc pl-5 mt-2 space-y-1">
              <li>Personal Information Dispute Mediation Committee (www.kopico.go.kr)</li>
              <li>Personal Information Infringement Report Center (privacy.kisa.or.kr / dial 118 without area code)</li>
              <li>Supreme Prosecutors’ Office Cybercrime Investigation Unit (http://www.spo.go.kr / dial 1301)</li>
              <li>Korean National Police Agency Cyber Bureau (http://cyberbureau.police.go.kr / dial 182)</li>
            </ul>
          </li>
        </ol>

        <p>
          <strong>Article 10 [Changes to and Notice of the Privacy Policy]</strong>
        </p>
        <p>
          If this Privacy Policy is added to, deleted from, or otherwise changed due to Company or government policy or
          changes in security technology, the Company will notify users through the application, website “Notices,”
          etc., at least seven (7) days before the changes take effect. The amended Privacy Policy becomes effective
          seven (7) days after the date of notice of the change.
        </p>

        <p>
          <strong>Article 11 [Collection and Use of Health Information (Biosignals)]</strong>
        </p>
        <p>
          ① With the user’s consent, the Company may access and collect the following{' '}
          <strong>health-related data (Health Data)</strong> to provide wellness features such as mind, stress, and
          sleep services within the Service:
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Heart rate</li>
          <li>Heart rate variability (HRV, e.g., SDNN, RMSSD, etc.)</li>
          <li>Stress- and wellness-related indicators (estimated values) calculated based on the above data</li>
          <li>
            (When the relevant features are used) The same types of biosignals provided by linked devices and platforms
            such as Apple Health (HealthKit), Google Fit / Health Connect, and Wear OS
          </li>
        </ul>
        <p>
          ② <strong>Collection methods</strong>
          <br />
          When the user grants permissions in the app, data is collected through linked wearable devices, OS health
          platforms, or in-app measurement features. If permission is refused, the relevant features may be limited,
          which may not affect use of other services.
        </p>
        <p>
          ③ <strong>Purpose of use</strong>
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Providing mind sessions and stress/relaxation-related features and presenting results</li>
          <li>
            Improving service quality, analyzing errors, and statistics/research (including processing in a form that
            does not directly identify individuals where possible)
            <br />※ This data is <strong>not for medical diagnosis or treatment</strong> and is used only for
            reference and wellness purposes.
          </li>
        </ul>
        <p>
          ④ <strong>Retention and destruction</strong>
          <br />
          Data is destroyed without delay upon membership withdrawal or when the purpose of collection and use has been
          achieved. Where retention is required under applicable laws, data is retained for the relevant period and then
          destroyed.
        </p>
        <p>
          ⑤ <strong>Provision to third parties</strong>
          <br />
          The Company does <strong>not sell</strong> users’ health-related data and does not provide it to third
          parties except where required by law or with the user’s separate consent. Where processing is entrusted for
          service operation, the entrusted parties and scope of work are disclosed in the Privacy Policy.
        </p>
        <p>
          ⑥ <strong>User rights</strong>
          <br />
          Users may withdraw health data access permissions through device/OS settings or in-app permission settings,
          and may request access, deletion, etc. from the Personal Information Protection Officer.
        </p>

        <p>
          <strong>Addendum</strong>
        </p>
        <p>This Privacy Policy applies from September 26, 2023.</p>
      </div>
    </main>
  );
}
